import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, firstValueFrom, lastValueFrom, Observable, of, Subject, takeUntil } from "rxjs";
import { PathStore } from "../../store/path.store";
import { HttpClient } from "@angular/common/http";
import { catchError, map, tap } from "rxjs/operators";
import {
  Carrello,
  PagamentoAnonimoRequest,
  PagamentoAreaPersonaleRequest,
  PosizioneDebitoria,
  AreaDiPagamentoViewModel,
  PagamentoAreaPersonaleResponse,
  PagamentoDirettoRequest,
  PagamentoDirettoResponse, TipologiaCarrello, ModelloPagamento, UserType, MyPayRequest
} from "../../models/payment-area/payment-area.model";
import { NgxSpinnerService } from "ngx-spinner";
import { StoreService } from "../store.service";
import { AuthService } from "../auth/auth.service";
import { ToastrService } from "ngx-toastr";
import { UserService } from '../user.service';
import { HttpService } from '../http.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class CartService implements OnDestroy {
  cartTipoToDelete: any = null
  pagamentoToRemove!: PosizioneDebitoria
  pagamentoDirettoRequest!: PagamentoDirettoRequest
  basketTypeCalculated: number = TipologiaCarrello.EnteDiContesto

  private readonly areCartEmpties = (c: Carrello[]): boolean => c.every((ca) => (ca.posizioniDebitorie?.length ?? 0) === 0)
  private carrelliSubject = new BehaviorSubject<Carrello[]>([])
  public carrelli$ = this.carrelliSubject.asObservable()
  public getAllPaymentsInCarts$ = this.carrelli$.pipe(
    map(c => c.flatMap(pd => pd.posizioniDebitorie)))
  public areCartsEmpties$ = this.carrelli$.pipe(map(this.areCartEmpties));
  public totalPayments$ = this.carrelli$.pipe(
    map(c => c.reduce((acc, curr) => acc + (curr.posizioniDebitorie?.length ?? 0), 0))
  );
  private maxCartsLength: { [key in TipologiaCarrello]: number } = {
    [TipologiaCarrello.EnteDiContesto]: 5,
    [TipologiaCarrello.MyPay]: 1,
    [TipologiaCarrello.MultiBeneficiario]: 1,
  }

  private destroy$ = new Subject<void>()

  constructor(
    private path: PathStore,
    private http: HttpClient,
    private router: Router,
    private spinner: NgxSpinnerService,
    private authService: AuthService,
    private storeService: StoreService,
    private toastr: ToastrService,
    private userService: UserService,
    private httpService: HttpService
  ) {
    // we need to update the cart every time a user login or logoff
    this.authService.decodedJWT$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getAreaPagamento().subscribe();
        if (!this.authService.isLogged()) {
          this.svuotaTuttiICarrelli(this.storeService.codIpa)
        }
      })
  }
  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
  public areCartEmptiesInStore() {
    return this.areCartEmpties(this.carrelliSubject.getValue());
  }
  public getPaymentUrlCallback() {
    return `${window.location.origin}/${this.storeService.codIpa ?? ''}/payment-return/cart/cart-result`;
  }
  getAreaPagamento(): Observable<Carrello[]> {
    return this.authService.isLogged() ? this.getAreaPagamentoOnline() : this.getAreaPagamentoOffline();
  }
  private getAreaPagamentoOnline(): Observable<Carrello[]> {
    return this.http.get<AreaDiPagamentoViewModel>(`${this.path.areaPagamentoUrl}`).pipe(
      map(responseDataAreaPagamenti => {
        const carrelli = this.mapResponseToCarrello(responseDataAreaPagamenti);
        this.carrelliSubject.next([...carrelli]);
        return carrelli;
      })
    );
  }
  private getAreaPagamentoOffline(): Observable<Carrello[]> {
    const snapshot = this.carrelliSubject.getValue();
    return of([...snapshot]);
  }
  putAreaPersonalArea(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria) {
    let url = `${this.path.areaPagamentoAggiungiAlCarrelloUrl}/${this.storeService.codIpa}/Carrello/${tipologiaCarrello}`
    return this.http.put<AreaDiPagamentoViewModel>(url, posizioneDebitoria)
  }

  readonly postPagamentoAreaPersonale = (req: PagamentoAreaPersonaleRequest): Observable<PagamentoAreaPersonaleResponse> => {
    return this.http.post<PagamentoAreaPersonaleResponse>(`${this.path.pagamentoAreaPersonaleUrl}`, req);
  }
  readonly postPagamentoAnonimo = (req: PagamentoAnonimoRequest, basketType: number): Observable<any> => {
    return this.http.post<any>(`${this.path.areaPagamentoAnonimaUrl}/pagamento/${basketType}`, req);
  };
  private readonly postPagamentoDiretto = (codiceIpa: string, req: PagamentoDirettoRequest): Observable<PagamentoDirettoResponse> => {
    return this.http.post<PagamentoDirettoResponse>(`${this.path.baseURLPosizioni}/areapagamentoservice/ente/${codiceIpa}/pagamento`, req);
  }
  private readonly postPagamentoDirettoAnonimo = (codiceIpa: string, req: PagamentoDirettoRequest): Observable<PagamentoDirettoResponse> => {
    return this.http.post<PagamentoDirettoResponse>(`${this.path.baseURLPosizioni}/areapagamentoservice/ente/${codiceIpa}/pagamentoAnonimo`, req);
  }

  sendPagamentoDiretto(req: PagamentoDirettoRequest): Observable<PagamentoDirettoResponse> {
    const sendPagamentoDiretto = this.authService.isLogged() ? this.postPagamentoDiretto : this.postPagamentoDirettoAnonimo;
    return sendPagamentoDiretto(this.storeService.codIpa ?? '', req);
  }
  deleteRimuoviDalCarrello(codiceIpa: string, tipologiaCarrello: number, idPosizione: string) {
    let url = `${this.path.areaPagamentoAggiungiAlCarrelloUrl}/${codiceIpa}/Carrello/${tipologiaCarrello}/posizione/${idPosizione}`
    return this.http.delete<any>(url)
  }
  rimuoviDalCarrello(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria) {
    this.spinner.show()
    this.deleteRimuoviDalCarrello(this.storeService.codIpa, tipologiaCarrello, posizioneDebitoria.id ?? '')
      .subscribe({
        next: response => {
          if (response) {
            // Se la chiamata al server va a buon fine, rimuovi l'elemento dal carrello
            const currentCarts = this.carrelliSubject.getValue();
            const carrello = currentCarts.find(c => c.tipologiaCarrello === tipologiaCarrello);
            if (carrello) {
              const index = carrello.posizioniDebitorie.findIndex(p => p.id === posizioneDebitoria.id);
              if (index >= 0) {
                carrello.posizioniDebitorie.splice(index, 1);
                carrello.posizioniDebitorie = [...carrello.posizioniDebitorie]
                this.carrelliSubject.next([...currentCarts]); // Aggiorna il BehaviorSubject con il valore corrente
              }
            }
          } else {
            console.error('la rimozione dal carrello non è andata a buon fine')
          }
          this.spinner.hide()
        },
        error: () => {
          this.spinner.hide()
        }
      })
  }
  rimuoviDalCarrelloAnonimo(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria) {
    this.spinner.show()
    // Se la chiamata al server va a buon fine, rimuovi l'elemento dal carrello
    const currentCarts = this.carrelliSubject.getValue();
    const carrello = currentCarts.find((c: Carrello) => c.tipologiaCarrello === tipologiaCarrello);
    if (carrello) {
      const index = carrello.posizioniDebitorie.findIndex((p: PosizioneDebitoria) => p.codiceServizio === posizioneDebitoria.codiceServizio);
      if (index >= 0) {
        carrello.posizioniDebitorie.splice(index, 1);
        this.carrelliSubject.next([...currentCarts]); // Aggiorna il BehaviorSubject con il valore corrente
      }
    }
    this.spinner.hide()
  }
  deleteSvuotaCarrello(codiceIpa: string, tipologiaCarrello: number) {
    let url = `${this.path.areaPagamentoAggiungiAlCarrelloUrl}/${codiceIpa}/Carrello/${tipologiaCarrello}`
    return this.http.delete<any>(url)
  }
  svuotaCarrello(codiceIpa: string, tipologiaCarrello: number) {
    this.spinner.show()
    this.deleteSvuotaCarrello(codiceIpa, tipologiaCarrello).subscribe({
      next: () => {
        // Se la chiamata al server va a buon fine, svuota il carrello specifico
        const currentCarts = this.carrelliSubject.getValue();
        const index = currentCarts.findIndex(c => c.tipologiaCarrello === tipologiaCarrello);
        if (index > -1) {
          currentCarts[index] = {
            ...currentCarts[index],
            posizioniDebitorie: []
          }
          this.carrelliSubject.next([...currentCarts]);
        }
        this.spinner.hide()
      },
      error: () => {
        this.spinner.hide()
      }
    });
  }
  svuotaCarrelloAnonimo(tipologiaCarrello: number) {
    const currentCarts = this.carrelliSubject.getValue();
    const index = currentCarts.findIndex(c => c.tipologiaCarrello === tipologiaCarrello);
    if (index > -1) {
      currentCarts[index] = {
        ...currentCarts[index],
        posizioniDebitorie: []
      }
      this.carrelliSubject.next([...currentCarts]);
    }
  }
  deleteEliminaCarrelli(codiceIpa: string) {
    let url = `${this.path.areaPagamentoAggiungiAlCarrelloUrl}/${codiceIpa}`
    return this.http.delete<any>(url)
  }
  svuotaTuttiICarrelli(codiceIpa: string) {
    if (this.authService.isLogged()) {
      this.svuotaTuttiICarrelliOnline(codiceIpa);
    } else {
      this.svuotaTuttiICarrelliOffline();
    }
  }
  private svuotaTuttiICarrelliOnline(codiceIpa: string) {
    this.spinner.show()
    // Supponendo che tu abbia i dati necessari per effettuare la chiamata DELETE
    this.deleteEliminaCarrelli(codiceIpa).subscribe({
      next: () => {
        // Se la chiamata al server va a buon fine, svuota tutti i carrelli
        const currentCarts = this.carrelliSubject.getValue();
        currentCarts.forEach((c: Carrello) => c.posizioniDebitorie = []);
        this.carrelliSubject.next([...currentCarts]); // Aggiorna il BehaviorSubject con il valore corrente
        this.spinner.hide()
      },
      error: () => {
        this.spinner.hide()
      }
    });
  }
  // Reset di tutti i carrelli
  public svuotaTuttiICarrelliOffline() {
    this.carrelliSubject.next([{
      posizioniDebitorie: [],
      tipologiaCarrello: 0,
      idTransazione: null,
      dataTransazione: null,
      codiceIpaEnte: this.storeService.codIpa,
      modelloPagamento: 1, // Reset
      areaDiPagamento: null,
      urlCallToClientWeb: null,
      urlReturnPaymentTORedirect: null
    }, {
      posizioniDebitorie: [],
      tipologiaCarrello: 1,
      idTransazione: null,
      dataTransazione: null,
      codiceIpaEnte: this.storeService.codIpa,
      modelloPagamento: 1, // Reset
      areaDiPagamento: null,
      urlCallToClientWeb: null,
      urlReturnPaymentTORedirect: null
    }, {
      posizioniDebitorie: [],
      tipologiaCarrello: 2,
      idTransazione: null,
      dataTransazione: null,
      codiceIpaEnte: this.storeService.codIpa,
      modelloPagamento: 1, // Reset
      areaDiPagamento: null,
      urlCallToClientWeb: null,
      urlReturnPaymentTORedirect: null
    }])
  }
  // controllo su "numeroAvviso" OR "iuv" invece che su "id" perchè l'ID cambia
  isInCart(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria): boolean {
    const carrello = this.carrelliSubject.getValue().find((c) => c.tipologiaCarrello === tipologiaCarrello);
    return carrello?.posizioniDebitorie?.some(
      (p: PosizioneDebitoria) => p.numeroAvviso === posizioneDebitoria.numeroAvviso && p.iuv === posizioneDebitoria.iuv
    ) === true;
    // return carrello?.posizioniDebitorie?.some((p: PosizioneDebitoria) => p.id === posizioneDebitoria.id) === true;
  }
  mapResponseToCarrello(responseData: AreaDiPagamentoViewModel): Carrello[] {
    let carrelloList: Carrello[] = [];
    // Mappiamo carrelloEnteDiContesto
    if (responseData.carrelloEnteDiContesto) {
      carrelloList.push(responseData.carrelloEnteDiContesto);
    }
    // Mappiamo carrelloMyPay
    if (responseData.carrelloMyPay) {
      carrelloList.push(responseData.carrelloMyPay);
    }
    // Mappiamo carrelloMultiBeneficiario
    if (responseData.carrelloMultiBeneficiario) {
      carrelloList.push(responseData.carrelloMultiBeneficiario);
    }
    return carrelloList;
  }
  mapResponseToCarrelloAnonimo(
    carrelloList: Carrello[],
    posizioneDebitoria: PosizioneDebitoria,
    tipologiaCarrello: number
  ): Carrello[] {
    const carrello = carrelloList[tipologiaCarrello];
    carrelloList[tipologiaCarrello] = {
      ...carrello,
      posizioniDebitorie: [...carrello.posizioniDebitorie, posizioneDebitoria],
    };
    return carrelloList;
  }

  // Deliverable-006 (modello di pagamento)
  public calculatePaymentModel(codiceFiscaleEntePayment: string, modelloPagamento: ModelloPagamento, spontaneo: boolean = false): ModelloPagamento {
    console.groupCollapsed('### calculatePaymentModel');
    console.log('userProfile', this.userService.userProfile);

    // Sulle posizioni debitorie per "PayNow", "codiceIpa" è sempre dell'ente di contesto; controllo il "codiceFiscaleEnte"
    // const codiceIpaAuthority = this.storeService.authority?.codiceIpaEnte ?? '';
    // console.log(`calculatePaymentModel - CODICE IPA: authority -> ${codiceIpaAuthority} - payment -> ${codiceIpaPayment}`);

    const codiceFiscaleAuthority = this.storeService.authority?.codiceFiscaleEnte ?? '';
    console.log(`CF: payment -> ${codiceFiscaleEntePayment} - CF: authority -> ${codiceFiscaleAuthority}`);

    let paymentModelCalculated: ModelloPagamento = modelloPagamento;

    // Deliverable-006: Inviare opportuno MODELLO DI PAGAMENTO
    if (this.authService.isLogged()) {
      // MODELLO 1: arriva come parametro della function ma lo sovrascrivo per sicurezza
      paymentModelCalculated = 1;
      if (this.storeService.authority?.autorizzazioneModelloTre == true) {
        // controllo profilo utente: modello 3 se isPagamentoVeloceAbilitato
        if(this.userService.userProfile?.isPagamentoVeloceAbilitato == true) {
          paymentModelCalculated = 3;
        } else {
          // Se sto paganto uno SPONTANEO, il modello è sempre 1 perchè è un servizio dell'ente di contesto
          if(spontaneo) {
            paymentModelCalculated = 1;
          } else {
            if (codiceFiscaleAuthority != codiceFiscaleEntePayment) {
              paymentModelCalculated = 3;
            }
          }
        }
      }
    } else {
      // MODELLO 1: arriva come parametro della function ma lo sovrascrivo per sicurezza
      // Pagamento Modello1 (ente di contesto) quando sto pagando un servizio offerto dall'ente
      paymentModelCalculated = 1;
      if (this.storeService.authority?.autorizzazioneModelloTre == true) {
        // Se sto paganto uno SPONTANEO, il modello è sempre 1 perchè è un servizio dell'ente di contesto
        if(spontaneo) {
          paymentModelCalculated = 1;
        } else {
          // 1.	Pagamento Modello3 se sto ricercando un codice ente differente da ente di contesto
          if (codiceFiscaleAuthority != codiceFiscaleEntePayment) {
            paymentModelCalculated = 3;
          }
        }
      }
    }
    console.log(`paymentModelCalculated: ${paymentModelCalculated}`);
    console.groupEnd();
    return paymentModelCalculated;
  }
  /**
   * Deliverable-006: tipologia carrello per Utente autenticato
   * Se non è "isMultibeneficiario" allora viene chiamato il servizio "isMyPay"
   * La posizione debitoria:
   *   - può avere la proprietà "isMultibeneficiario"
   *   - ha la proprietà "codiceEntePlugAndPay"
   *   - ha la proprietà "codiceServizio"
   * @param tipologiaCarrello
   * @param posizioneDebitoria
   */
  aggiungiAlCarrello(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria) {
    this.calculateBasketTypeAndAddToBasket(tipologiaCarrello, posizioneDebitoria, UserType.Authenticated);
  }
  /**
   * Deliverable-006: tipologia carrello per Utente anonimo (Servizi senza registrazione)
   * Viene chiamato il servizio "isMyPay" (chiamare SEMPRE anche se è uno spontaneo)
   * Il pagamento spontaneo:
   *   - non ha la proprietà "isMultibeneficiario"
   *   - non ha la proprietà "codiceEntePlugAndPay" che però viene impostata sul BE con quella dell'Ente
   *   - ha la proprietà "codiceServizio"
   * @param tipologiaCarrello
   * @param posizioneDebitoria
   */
  aggiungiAlCarrelloAnonimo(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria) {
    this.calculateBasketTypeAndAddToBasket(tipologiaCarrello, posizioneDebitoria, UserType.Anonymous);
  }
  private calculateBasketTypeAndAddToBasket(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria, userAuthentication: string) {
    console.groupCollapsed('### calculateBasketTypeAndAddToBasket');
    console.log('tipologiaCarrello', tipologiaCarrello);
    console.log('posizioneDebitoria', posizioneDebitoria);
    console.log('userAuthentication', userAuthentication);
    // [TODO]: calcolare e memorizzare la tipologia di carrello
    let basketType = tipologiaCarrello;

    if (posizioneDebitoria && posizioneDebitoria.isMultibeneficiario && posizioneDebitoria.isMultibeneficiario === true) {
      basketType = TipologiaCarrello.MultiBeneficiario
      this.basketTypeCalculated = basketType
      console.log('MultiBeneficiario - basketType', basketType);
      if (userAuthentication == UserType.Authenticated) {
        this.addToBasket(basketType, posizioneDebitoria)
      } else {
        this.addToAnonymousBasket(basketType, posizioneDebitoria)
      }
    } else {
      basketType = TipologiaCarrello.EnteDiContesto
      this.basketTypeCalculated = basketType
      this.spinner.show()
      const myPayPayload: MyPayRequest = {
        codiceEnte: posizioneDebitoria.codiceEntePlugAndPay ?? '',
        codiceServizio: posizioneDebitoria.codiceServizio ?? ''
      }
      this.getIsMyPay(myPayPayload)
        .subscribe({
          next: res => {
            this.spinner.hide()
            console.log('getIsMyPay', res);
            if (res && res === true) {
              basketType = TipologiaCarrello.MyPay
              this.basketTypeCalculated = basketType
            }
            console.log('MyPay - basketType', basketType);
            if (userAuthentication == UserType.Authenticated) {
              this.addToBasket(basketType, posizioneDebitoria)
            } else {
              this.addToAnonymousBasket(basketType, posizioneDebitoria)
            }
          },
          error: (err) => {
            this.spinner.hide()
            console.error('getIsMyPay', err);
            console.log('EnteDiContesto - basketType', basketType);
            if (userAuthentication == UserType.Authenticated) {
              this.addToBasket(basketType, posizioneDebitoria)
            } else {
              this.addToAnonymousBasket(basketType, posizioneDebitoria)
            }
          }
        });
    }
    console.groupEnd();
  }
  public calculateBasketTypeAndPay(posizioneDebitoria: PosizioneDebitoria) {
    console.groupCollapsed('### calculateBasketTypeAndPay');
    console.log('posizioneDebitoria', posizioneDebitoria);
    this.basketTypeCalculated = TipologiaCarrello.EnteDiContesto;

    // [x]: fix email for datiContribuente
    console.log('posizioneDebitoria email', posizioneDebitoria?.datiContribuente?.email);
    if(posizioneDebitoria && posizioneDebitoria.datiContribuente && posizioneDebitoria.datiContribuente.email == '') {
      posizioneDebitoria.datiContribuente.email = this.userService.userProfile?.email ?? '';
    }
    // TODO: aggiugere controllo?
    // if(posizioneDebitoria?.datiContribuente?.email == '') {
    //   this.toastr.error('Mail utente non trovata! Aggiungere la mail accedendo a DATI PERSONALI');
    //   return;
    // }
    console.groupEnd();
    if (posizioneDebitoria && posizioneDebitoria.isMultibeneficiario && posizioneDebitoria.isMultibeneficiario === true) {
      this.basketTypeCalculated = TipologiaCarrello.MultiBeneficiario
      // imposto "tipologia di carrello" e vado al pagamento
      this.pagamentoDirettoRequest.tipologiaCarrello = this.basketTypeCalculated;
      if (this.router.url.includes('/services-without-registration')) {
        this.router.navigate([`${this.storeService.codIpa}/services-without-registration/payment-area/cart/pay-now`])
      }
      if (this.router.url.includes('/personal')) {
        this.router.navigate([`${this.storeService.codIpa}/personal/payment-area/cart/pay-now`])
      }
    } else {
      this.spinner.show()
      const myPayPayload: MyPayRequest = {
        codiceEnte: posizioneDebitoria.codiceEntePlugAndPay ?? '',
        codiceServizio: posizioneDebitoria.codiceServizio ?? ''
      }
      this.getIsMyPay(myPayPayload)
        .subscribe({
          next: res => {
            this.spinner.hide()
            if (res && res === true) {
              this.basketTypeCalculated = TipologiaCarrello.MyPay
            }
            // imposto "tipologia di carrello" e vado al pagamento
            if(this.pagamentoDirettoRequest && this.pagamentoDirettoRequest.tipologiaCarrello) {
              this.pagamentoDirettoRequest.tipologiaCarrello = this.basketTypeCalculated;
            }
            if (this.router.url.includes('/services-without-registration')) {
              this.router.navigate([`${this.storeService.codIpa}/services-without-registration/payment-area/cart/pay-now`])
            }
            if (this.router.url.includes('/personal')) {
              this.router.navigate([`${this.storeService.codIpa}/personal/payment-area/cart/pay-now`])
            }
          },
          error: (err) => {
            this.spinner.hide()
            console.groupCollapsed('### calculateBasketTypeAndPay - getIsMyPay');
            console.error('getIsMyPay', err);
            console.log('calculateBasketType - EnteDiContesto - basketTypeCalculated', this.basketTypeCalculated);
            console.groupEnd();
            // imposto "tipologia di carrello" e vado al pagamento
            if(this.pagamentoDirettoRequest && this.pagamentoDirettoRequest.tipologiaCarrello) {
              this.pagamentoDirettoRequest.tipologiaCarrello = this.basketTypeCalculated;
            }
            if (this.router.url.includes('/services-without-registration')) {
              this.router.navigate([`${this.storeService.codIpa}/services-without-registration/payment-area/cart/pay-now`])
            }
            if (this.router.url.includes('/personal')) {
              this.router.navigate([`${this.storeService.codIpa}/personal/payment-area/cart/pay-now`])
            }
          }
        });
    }

  }
  private getIsMyPay(req: MyPayRequest): Observable<boolean> {
    const codiceIpaEnte = this.storeService.authority?.codiceIpaEnte ?? '';
    const url = `${this.path.areaPagamentoIsMyPayUrl}/${codiceIpaEnte}`;
    return this.http.post<boolean>(`${url}`, req);
  }
  // private getIsMyPay(codiceEntePlugAndPay: string, codiceServizio: string): Observable<boolean> {
  //   const url = `${this.path.areaPagamentoIsMyPayUrl}/${codiceEntePlugAndPay}/${codiceServizio}`;
  //   return this.http.get<boolean>(url)
  // }
  addToBasket(tipologiaCarrello: number, posizioneDebitoria: PosizioneDebitoria) {
    this.spinner.show();
    if (this.isCartFilled(tipologiaCarrello)) {
      this.spinner.hide()
      this.toastr.info('Hai raggiunto il limite di elementi nel carrello per questa tipologia di posizione debitoria')
    } else {
      this.putAreaPersonalArea(tipologiaCarrello, posizioneDebitoria)
        .subscribe({
          next: response => {
            if (response) {
              const carrelli = this.mapResponseToCarrello(response);
              this.carrelliSubject.next([...carrelli]);
              this.toastr.success('Posizione aggiunta al carrello')
              this.spinner.hide();
            } else {
              this.toastr.error('Errore in aggiunta al carrello')
            }
          },
          error: () => {
            this.spinner.hide();
          }
        });
    }
  }
  addToAnonymousBasket(
    tipologiaCarrello: number,
    posizioneDebitoria: PosizioneDebitoria
  ) {
    this.spinner.show();

    if (this.isCartFilled(tipologiaCarrello)) {
      this.spinner.hide();
      this.toastr.info(
        'Hai raggiunto il limite di elementi nel carrello per questa tipologia di posizione debitoria'
      );
      return;
    }

    const oldCarrelli = this.carrelliSubject.getValue();
    const oldCarrello = oldCarrelli[tipologiaCarrello];

    // FIX: controllo anche sugli spontanei in modo da evitare duplicati
    // const alreadyExists = oldCarrello.posizioniDebitorie.some(p => {
    //   const hasAvvisoP = !!p.numeroAvviso;
    //   const hasAvvisoNew = !!posizioneDebitoria.numeroAvviso;
    //   // CASE 1: both have numeroAvviso -> unique by numeroAvviso
    //   if (hasAvvisoP && hasAvvisoNew) {
    //     return p.numeroAvviso === posizioneDebitoria.numeroAvviso;
    //   }
    //   // Normalize arrays (avoid undefined issues)
    //   const idsA = p.idsTariffa ?? [];
    //   const idsB = posizioneDebitoria.idsTariffa ?? [];
    //   // CASE 2: spontaneous -> compare stable keys
    //   const sameBaseKeys =
    //     p.codiceServizio === posizioneDebitoria.codiceServizio &&
    //     p.importo === posizioneDebitoria.importo &&
    //     p.datiContribuente?.codiceFiscalePartitaIva ===
    //       posizioneDebitoria.datiContribuente?.codiceFiscalePartitaIva;
    //   const sameIdsTariffa =
    //     idsA.length === idsB.length &&
    //     idsA.every((id, index) => id === idsB[index]);
    //   return sameBaseKeys && sameIdsTariffa;
    // });
    
    // FIX: corretto controllo per spontaneo (senza numeroAvviso)
    const alreadyExists = oldCarrello.posizioniDebitorie.some((p) =>
      p.numeroAvviso != null &&
      posizioneDebitoria.numeroAvviso != null &&
      p.numeroAvviso === posizioneDebitoria.numeroAvviso
    );

    // Se già esiste → messaggio e stop
    if (alreadyExists) {
      this.spinner.hide();
      this.toastr.info('La posizione è già presente nel carrello');
      return;
    }

    // Se NON esiste → aggiungi
    const carrelliAggiornati = this.mapResponseToCarrelloAnonimo(
      oldCarrelli,
      posizioneDebitoria,
      tipologiaCarrello
    );

    this.carrelliSubject.next([...carrelliAggiornati]);

    setTimeout(() => this.spinner.hide(), 500);
    this.toastr.success('Posizione aggiunta al carrello');
  }
  isCartFilled(tipologiaCarrello: TipologiaCarrello): boolean {
    const carrelli = this.carrelliSubject.value;
    const carrello = carrelli.find((c) => c.tipologiaCarrello === tipologiaCarrello);
    if (!carrello) {
      return false;
    }
    if(carrello.posizioniDebitorie && carrello.posizioniDebitorie != null) {
      return carrello.posizioniDebitorie.length >= this.maxCartsLength[tipologiaCarrello];
    } else {
      return false;
    }
  }
}
